import apiConfig from '../Data/Api/config';
import { getCloudfrontProxyUrl, isCloudfrontAssetUrl } from './cloudfrontAsset';

const MAX_DETAIL_LENGTH = 4000;
const SESSION_STORAGE_KEY = 'nb-client-event-session';

let cachedSessionId = null;

const safeStringify = (value) => {
    try {
        return JSON.stringify(value);
    } catch (err) {
        return String(value);
    }
};

const compactDetails = (details) => {
    if (!details) return {};
    const json = safeStringify(details);
    if (json.length <= MAX_DETAIL_LENGTH) return details;
    return {
        truncated: true,
        text: json.substring(0, MAX_DETAIL_LENGTH),
    };
};

const getParam = (name) => {
    try {
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)');
        const results = regex.exec(window.location.href);
        if (!results || !results[2]) return null;
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    } catch (err) {
        return null;
    }
};

const isSameOrigin = (url) => {
    try {
        const link = document.createElement('a');
        link.href = url;
        return link.protocol === window.location.protocol && link.host === window.location.host;
    } catch (err) {
        return false;
    }
};

const createSessionId = () => {
    return String(Date.now()) + '-' + Math.random().toString(36).substring(2, 10);
};

const getSessionId = () => {
    if (cachedSessionId) return cachedSessionId;
    try {
        cachedSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (!cachedSessionId) {
            cachedSessionId = createSessionId();
            window.sessionStorage.setItem(SESSION_STORAGE_KEY, cachedSessionId);
        }
        return cachedSessionId;
    } catch (err) {
        cachedSessionId = createSessionId();
        return cachedSessionId;
    }
};

const getConnectionInfo = () => {
    try {
        const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
        if (!connection) return null;
        return {
            effectiveType: connection.effectiveType,
            type: connection.type,
            downlink: connection.downlink,
            rtt: connection.rtt,
            saveData: connection.saveData,
        };
    } catch (err) {
        return null;
    }
};

const postWithXHR = (url, body) => {
    try {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.withCredentials = false;
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.send(body);
    } catch (err) {
    }
};

export const logClientEvent = (eventName, details = {}) => {
    try {
        if (!eventName || typeof window === 'undefined') return;

        const payload = {
            eventName,
            productReleaseCode: details.productReleaseCode || getParam('id') || getParam('c'),
            sessionId: getSessionId(),
            severity: details.severity || 'info',
            path: window.location.pathname,
            href: window.location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            details: compactDetails(Object.assign({}, details, {
                visibilityState: document.visibilityState,
                connection: getConnectionInfo(),
            })),
            occurredAt: new Date().toISOString(),
        };

        const url = apiConfig.url + apiConfig.eCLIENT_EVENT_LOG;
        const body = JSON.stringify(payload);
        const sameOrigin = isSameOrigin(url);

        if (window.fetch) {
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                keepalive: true,
                mode: 'cors',
                credentials: 'omit',
            }).catch(() => {});
            return;
        }

        if (sameOrigin && navigator.sendBeacon && typeof Blob !== 'undefined') {
            try {
                const blob = new Blob([body], { type: 'application/json' });
                if (navigator.sendBeacon(url, blob)) return;
            } catch (err) {
            }
        }

        postWithXHR(url, body);
    } catch (err) {
    }
};

const applyImageFallback = (target) => {
    try {
        if (!target || target.tagName !== 'IMG') return null;
        const src = target.currentSrc || target.src;
        if (!isCloudfrontAssetUrl(src)) {
            if (target.__nbCloudfrontFallbackTried) {
                logClientEvent('cloudfront_img_fallback_failed', {
                    severity: 'error',
                    src,
                });
            }
            return null;
        }
        if (target.__nbCloudfrontFallbackTried) return null;

        const proxyUrl = getCloudfrontProxyUrl(src);
        target.__nbCloudfrontFallbackTried = true;
        logClientEvent('cloudfront_img_fallback_applied', {
            severity: 'warn',
            src,
            proxyUrl,
        });
        target.src = proxyUrl;
        return proxyUrl;
    } catch (err) {
        return null;
    }
};

export const installClientEventLogging = () => {
    try {
        if (typeof window === 'undefined' || window.__nbClientEventLoggingInstalled) return;
        window.__nbClientEventLoggingInstalled = true;

        logClientEvent('bundle_loaded');

        window.addEventListener('error', (event) => {
            const target = event.target || {};
            const isResourceError = target && target !== window && target.tagName;
            const fallbackUrl = isResourceError ? applyImageFallback(target) : null;
            logClientEvent(isResourceError ? 'resource_error' : 'js_error', {
                severity: isResourceError ? 'warn' : 'error',
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                tagName: target.tagName,
                src: target.src || target.href,
                fallbackUrl,
            });
        }, true);

        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason || {};
            logClientEvent('unhandledrejection', {
                severity: 'error',
                message: reason.message || String(reason),
                stack: reason.stack,
            });
        });
    } catch (err) {
    }
};
